import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "components/common/Toast";

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

const ReflectCommission = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [editingTierId, setEditingTierId] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [savingTierId, setSavingTierId] = useState(null);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    setIsLoading(true);
    try {
      const response = await ApiRequest.post("reflect_commission?operation=GET");
      if (response?.data?.status === "success") {
        setTiers(response.data.data || []);
      } else {
        Toast.fire({
          title: response?.data?.reason || "Failed to load commission settings",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching Reflect commission:", error);
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (tier) => {
    setEditingTierId(tier.tier_id);
    setEditedValue(String(tier.commission));
  };

  const handleCancel = () => {
    setEditingTierId(null);
    setEditedValue("");
  };

  const handleSave = async (tier) => {
    const commission = Number(editedValue);
    if (!Number.isFinite(commission) || commission < 0) {
      Toast.fire({ title: "Enter a valid commission (0 or more)", icon: "error" });
      return;
    }

    setSavingTierId(tier.tier_id);
    try {
      const response = await ApiRequest.post(
        "reflect_commission?operation=UPDATE",
        { tier_id: tier.tier_id, commission },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response?.data?.status === "success") {
        Toast.fire({ title: "Commission updated successfully!", icon: "success" });
        setTiers((prev) =>
          prev.map((t) =>
            t.tier_id === tier.tier_id ? { ...t, commission } : t
          )
        );
        handleCancel();
      } else {
        Toast.fire({
          title: response?.data?.reason || "Failed to update commission",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error updating Reflect commission:", error);
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSavingTierId(null);
    }
  };

  return (
    <Box
      sx={{
        margin: "20px auto",
        padding: "30px",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
        borderRadius: "10px",
        color: "#000",
        width: "100%",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Reflect Commission
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Define the commission charged on Reflect wallet transfers per amount
        range. The commission is the total fee added on top of the transfer and
        is split 50/50 between the system and the seller. Admin only.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><strong>Amount Range (₪)</strong></TableCell>
                <TableCell><strong>Commission (₪)</strong></TableCell>
                <TableCell><strong>System Share (₪)</strong></TableCell>
                <TableCell><strong>Seller Share (₪)</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.map((tier) => {
                const isEditing = editingTierId === tier.tier_id;
                const isSaving = savingTierId === tier.tier_id;
                const displayValue = isEditing
                  ? Number(editedValue)
                  : tier.commission;

                return (
                  <TableRow key={tier.tier_id}>
                    <TableCell>{tier.label}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editedValue}
                          onChange={(e) => setEditedValue(e.target.value)}
                          inputProps={{ min: 0, step: "0.5" }}
                          sx={{ width: 120 }}
                          autoFocus
                        />
                      ) : (
                        <Typography variant="body2">
                          {formatNumber(tier.commission)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatNumber(displayValue / 2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatNumber(displayValue / 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {isEditing ? (
                        <>
                          <Tooltip title="Save">
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => handleSave(tier)}
                                size="small"
                                disabled={isSaving}
                              >
                                {isSaving ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <SaveIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <span>
                              <IconButton
                                color="secondary"
                                onClick={handleCancel}
                                size="small"
                                disabled={isSaving}
                              >
                                <CancelIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(tier)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {tiers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No commission tiers found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ReflectCommission;
